import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Col, Row } from 'react-bootstrap';
import { Label } from 'common/components/texts/Texts';
import InputError from 'common/components/inputError/InputError';
import { TextInputController } from 'common/components/textInput/TextInput';
import { MoneyInputController } from 'common/components/moneyInput/MoneyInput';
import styles from './BudgetComponentDetails.module.scss'
import { FamilyDto } from 'api/families/models/FamilyDto';
import { FamilySelectInputOption } from 'common/components/familySelector/FamilySelector';
import { SelectInputController } from 'common/components/selectInput/SelectInput';
import { BudgetComponentDto } from 'api/budgets/models/BudgetComponentDto';
import { MAX_DECIMAL_PLACES, MIN_DECIMAL_PLACES } from 'Config';
import { useDispatch } from 'react-redux';
import { calculateAllLinesTotals, updateBudget } from 'store/budget/action';

interface Props {
    type: 'create' | 'details' | 'edit' | 'version' | undefined;
    families: FamilyDto[];
    onChangeFamily: (id: string | null | undefined) => void;
    onChangeSubFamily: (id: string | null | undefined) => void;
}

function BudgetComponentDetails({ type, families, onChangeFamily, onChangeSubFamily }: Props): JSX.Element | null {
    const { t } = useTranslation();
    const form = useFormContext<BudgetComponentDto>();
    const isDetails = type === 'details' || type === 'version';
    const dispatch = useDispatch();

    const onBlurDecimalPlaces = (v: number | undefined | null) => {
        dispatch(updateBudget({
            decimalPlaces: v ?? 0,
        }));
        dispatch(calculateAllLinesTotals());
    };
    const errors = form.formState.errors;
    const familyId = form.watch('familyId');
    const familiesOptions: FamilySelectInputOption[] = families.map((x: FamilyDto) => {
        return {
            label: x.name,
            value: x.id,
            isComponent: x.isComponent,
            isElement: x.isElement,
            subFamilies: x.subFamilies?.map((y: FamilyDto) => {
                return {
                    label: y.name,
                    value: y.id,
                    subFamilies: [],
                    isComponent: x.isComponent,
                    isElement: x.isElement,
                }
            }) ?? []
        }
    }) ?? [];
    const subFamiliesOptions = familiesOptions.find(x => x.value === familyId)?.subFamilies ?? [];

    return (
        <div className={styles.container}>
            <Row className="mb-3">
                <Col sm={12} md={12} xl={12}>
                    <Label space>{t('budgets.budget.description')}{!isDetails ? '*' : ''}</Label>
                    <TextInputController
                        name='description'
                        control={form.control}
                        placeholder={t('budgets.budget.description')}
                        disabled={isDetails}
                        rules={{ required: true, maxLength: 500 }}
                        hasError={Boolean(errors.description)}
                    />
                    <InputError error={errors.description} maxLength={500} />
                </Col>
            </Row>
            <Row className="mb-3">
                <Col xs={12} sm={12} md={6}>
                    <Label space>{t('elements.family')}</Label>
                    <SelectInputController
                        name={'familyId'}
                        control={form.control as any}
                        options={familiesOptions}
                        disabled={isDetails}
                        onChange={(id) => onChangeFamily(id)}
                        hasError={Boolean(errors.familyId)}
                    />
                    <InputError error={errors.familyId} />
                </Col>
                <Col xs={12} sm={12} md={6}>
                    <Label space>{t('elements.sub_family')}</Label>
                    <SelectInputController
                        name={'subFamilyId'}
                        control={form.control as any}
                        options={subFamiliesOptions}
                        disabled={isDetails}
                        onChange={(id) => onChangeSubFamily(id)}
                        hasError={Boolean(errors.subFamilyId)}
                    />
                    <InputError error={errors.subFamilyId} />
                </Col>
            </Row>
            <Row className="mb-3">
                <Col sm={12} md={6}>
                    <Label space>{t('budgets.budget.decimal_places')}</Label>
                    <MoneyInputController
                        name="decimalPlaces"
                        disabled={isDetails}
                        control={form.control}
                        decimalScale={0}
                        hideUnitsDrop
                        onBlur={onBlurDecimalPlaces}
                        isAllowed={({ floatValue }) => {
                            return (
                                !!floatValue && (floatValue >= MIN_DECIMAL_PLACES && floatValue <= MAX_DECIMAL_PLACES)
                            );
                        }}
                    />
                </Col>
            </Row>
        </div>
    );
}

export default BudgetComponentDetails;
